/**
 * Brain → Company API client.
 *
 * The 3 singletons (profile / philosophy / headcount) live on the
 * Organization doc. PUT replaces the whole payload — payload shapes are
 * iterating fast and JSON-merge would surprise the demo.
 *
 * Past hires are a real collection.
 *
 * baseURL already includes /api, so paths here start with /v2/...
 */

import api from '@/services/api';
import type {
    OrgProfile,
    HiringPhilosophy,
    HeadcountPlan,
    PastHire,
} from '@/modules/brain/data/seedCompany';

const BASE = '/v2/brain/company';

export type CompanySingletonKind = 'profile' | 'philosophy' | 'headcount';

type SingletonEnvelope<T> = { value: T | null };

async function getSingleton<T>(kind: CompanySingletonKind): Promise<T | null> {
    const { data } = await api.get<SingletonEnvelope<T>>(`${BASE}/${kind}`);
    return data.value;
}

async function putSingleton<T>(kind: CompanySingletonKind, value: T | null): Promise<T | null> {
    const { data } = await api.put<SingletonEnvelope<T>>(`${BASE}/${kind}`, { value });
    return data.value;
}

// Mongo returns _id; the page renders <li key={h.id}>, so without this the
// seed→API swap blows away every key and the roster flickers.
function normalizeHire(raw: PastHire & { _id?: string }): PastHire {
    if (raw.id) return raw;
    return { ...raw, id: raw._id ?? '' };
}

export const companyApi = {
    getProfile: () => getSingleton<OrgProfile>('profile'),
    putProfile: (value: OrgProfile | null) => putSingleton<OrgProfile>('profile', value),

    getPhilosophy: () => getSingleton<HiringPhilosophy>('philosophy'),
    putPhilosophy: (value: HiringPhilosophy | null) =>
        putSingleton<HiringPhilosophy>('philosophy', value),

    getHeadcount: () => getSingleton<HeadcountPlan>('headcount'),
    putHeadcount: (value: HeadcountPlan | null) =>
        putSingleton<HeadcountPlan>('headcount', value),

    listHires: async (filters?: {
        team?: string;
        sourceChannel?: string;
        status?: 'active' | 'departed';
    }): Promise<PastHire[]> => {
        const { data } = await api.get<PastHire[]>(`${BASE}/hires`, { params: filters });
        return data.map(normalizeHire);
    },

    getHire: async (id: string): Promise<PastHire> => {
        const { data } = await api.get<PastHire>(`${BASE}/hires/${id}`);
        return normalizeHire(data);
    },

    createHire: async (payload: Omit<PastHire, 'id'> & { id?: string }): Promise<PastHire> => {
        const { data } = await api.post<PastHire>(`${BASE}/hires`, payload);
        return normalizeHire(data);
    },

    updateHire: async (id: string, updates: Partial<PastHire>): Promise<PastHire> => {
        const { data } = await api.patch<PastHire>(`${BASE}/hires/${id}`, updates);
        return normalizeHire(data);
    },

    deleteHire: async (id: string): Promise<void> => {
        await api.delete(`${BASE}/hires/${id}`);
    },
};

/**
 * Helper: fetch with a hard fallback. Used by the YC demo pages so a Mongo
 * blip during the demo doesn't blank the screen — they show seed data
 * instead and quietly log the error.
 */
export async function fetchWithFallback<T>(
    fetcher: () => Promise<T | null>,
    fallback: T,
    label: string,
): Promise<{ value: T; fromApi: boolean }> {
    try {
        const value = await fetcher();
        if (value === null || value === undefined) {
            return { value: fallback, fromApi: false };
        }
        return { value, fromApi: true };
    } catch (err) {
        console.warn(`[brain.company] ${label} fetch failed, using seed fallback`, err);
        return { value: fallback, fromApi: false };
    }
}
